import { bindModalBackdrop, closeModal, element, openModal } from "./ui";

const THEME_KEY = "xianyu-theme";
const COLOR_MODE_KEY = "xianyu-color-mode";

const COLOR_MODES = [
  { id: "system", label: "跟随系统" },
  { id: "light", label: "浅色" },
  { id: "dark", label: "深色" },
] as const;

const THEMES = [
  { id: "default", label: "默认 · 靛蓝", swatch: "#4338ca" },
  { id: "linear", label: "Linear · 紫蓝", swatch: "#4f56c9" },
  { id: "stripe", label: "Stripe · 蓝紫", swatch: "#5751e6" },
  { id: "vercel", label: "Vercel · 黑白蓝", swatch: "#0060df" },
  { id: "notion", label: "Notion · 暖灰蓝", swatch: "#1a73c7" },
  { id: "figma", label: "Figma · 创意紫", swatch: "#7c3aed" },
  { id: "supabase", label: "Supabase · 青绿", swatch: "#24a871" },
  { id: "shopify", label: "Shopify · 品牌绿", swatch: "#006e52" },
  { id: "airbnb", label: "Airbnb · 珊瑚红", swatch: "#e5484d" },
  { id: "sky", label: "Sky · 天蓝", swatch: "#0369a1" },
  { id: "rose", label: "Rose · 玫瑰粉", swatch: "#be123c" },
] as const;

type ColorMode = (typeof COLOR_MODES)[number]["id"];

function isColorMode(value: string): value is ColorMode {
  return COLOR_MODES.some((mode) => mode.id === value);
}

function darkMode(mode: ColorMode): boolean {
  if (mode === "dark") return true;
  if (mode === "light") return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function applyColorMode(value: string): void {
  const mode = isColorMode(value) ? value : "system";
  document.documentElement.dataset.colorMode = mode;
  document.documentElement.classList.toggle("theme-dark", darkMode(mode));
  localStorage.setItem(COLOR_MODE_KEY, mode);
  document.querySelectorAll<HTMLButtonElement>(".appearance-mode").forEach((button) => {
    const active = button.dataset.mode === mode;
    button.classList.toggle("active", active);
    button.setAttribute("aria-pressed", String(active));
  });
}

function applyTheme(theme: string): void {
  const selected = THEMES.some((option) => option.id === theme) ? theme : "vercel";
  document.documentElement.dataset.theme = selected;
  localStorage.setItem(THEME_KEY, selected);
  document.querySelectorAll<HTMLButtonElement>(".theme-option").forEach((button) => {
    const active = button.dataset.accent === selected;
    button.classList.toggle("active", active);
    button.setAttribute("aria-pressed", String(active));
  });
}

export function initTheme(): void {
  element<HTMLElement>("appearanceModes").innerHTML = COLOR_MODES.map(
    (mode) => `<button type="button" class="appearance-mode" data-mode="${mode.id}" aria-pressed="false">${mode.label}</button>`,
  ).join("");
  element<HTMLElement>("themeGrid").innerHTML = THEMES.map(
    (theme) => `<button type="button" class="theme-option" data-accent="${theme.id}" aria-pressed="false"><span class="theme-swatch" style="background:${theme.swatch}"></span><span class="theme-label">${theme.label}</span></button>`,
  ).join("");
  applyColorMode(localStorage.getItem(COLOR_MODE_KEY) || "system");
  applyTheme(localStorage.getItem(THEME_KEY) || "vercel");
  element<HTMLElement>("appearanceModes").addEventListener("click", (event) => {
    const button = event.target instanceof Element
      ? event.target.closest<HTMLButtonElement>("[data-mode]")
      : null;
    if (button?.dataset.mode) applyColorMode(button.dataset.mode);
  });
  element<HTMLElement>("themeGrid").addEventListener("click", (event) => {
    const button = event.target instanceof Element
      ? event.target.closest<HTMLButtonElement>("[data-accent]")
      : null;
    if (button?.dataset.accent) applyTheme(button.dataset.accent);
  });
  window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", () => {
    const mode = localStorage.getItem(COLOR_MODE_KEY) || "system";
    if (mode === "system") applyColorMode(mode);
  });
  const modal = element<HTMLElement>("settingsModal");
  element<HTMLButtonElement>("settingsBtn").addEventListener("click", () => openModal(modal));
  element<HTMLButtonElement>("settingsCloseBtn").addEventListener("click", () => closeModal(modal));
  bindModalBackdrop(modal, () => closeModal(modal));
}
